/* MusicPicker — stacked, full-width music rows.
   Replaces the earlier dropdown. Each row is a premium 3D-lift card:
     ▶ / ⏸  play-preview  |  track name · mood · description  |  Select
   Only one track can play at a time (module-scope singleton lives in
   `./useMusicPreview`). The selected track is visually highlighted with
   a warm-gold ring + inner surface glow. */
import React, { useEffect, useMemo, useState } from 'react';
import { Play, Pause, Check, Music as MusicIcon, VolumeX, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { useMusicPreview } from './useMusicPreview';

export function MusicPicker({
  value,
  onChange,
  testIdPrefix = 'music',
  // Optional: render a pseudo-row for the memorial's custom uploaded track.
  customTrack = null,
}) {
  const [tracks, setTracks] = useState([]);
  const [categories, setCategories] = useState([]);
  // iter97.16 — accordion state. Default: only the category containing
  // the currently-selected track is expanded; everything else is
  // collapsed so the picker fits in a small slice of the page.
  const [openCategory, setOpenCategory] = useState(null);
  const { playing, toggle, pause } = useMusicPreview();

  useEffect(() => {
    api
      .get('/music/tracks')
      .then(({ data }) => {
        setTracks(data.tracks || []);
        setCategories(data.categories || []);
      })
      .catch(() => setTracks([]));
  }, []);

  // iter97.15 — Group rows by category. The custom uploaded track sits
  // in its own pinned "Your song" slot at the top; library + silence
  // tracks are grouped under their server-defined categories.
  const { grouped, orderedCategories } = useMemo(() => {
    const grouped = new Map();
    const ensureCategory = (cat) => {
      if (!grouped.has(cat)) grouped.set(cat, []);
      return grouped.get(cat);
    };
    for (const t of tracks) {
      ensureCategory(t.category || 'Piano & Classical').push(t);
    }
    // Preserve server-provided category ordering; tack on any unknown
    // categories at the end so we don't accidentally hide a track.
    const orderedCategories = [
      ...categories.filter((c) => grouped.has(c)),
      ...[...grouped.keys()].filter((c) => !categories.includes(c)),
    ];
    return { grouped, orderedCategories };
  }, [tracks, categories]);

  // iter97.16 — On first render (and whenever the selected track
  // changes), expand the category that contains the current selection
  // so the user sees their pick. Otherwise keep the first category
  // open by default.
  useEffect(() => {
    if (!orderedCategories.length) return;
    const containing = orderedCategories.find((cat) =>
      (grouped.get(cat) || []).some((t) => t.track_id === value)
    );
    setOpenCategory(containing || orderedCategories[0]);
  }, [tracks.length, value, orderedCategories, grouped]);

  if (!tracks.length) {
    return (
      <div className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.5)]">
        Loading music…
      </div>
    );
  }

  // iter97.15 — Single row renderer reused inside each category section.
  const renderRow = (t) => {
    const isCustom = t.kind === 'custom';
    const isSilence = t.kind === 'silence';
    const isSelected = value === t.track_id || (isCustom && value === '__custom__');
    const isPlaying = !!(playing && playing.track_id === t.track_id);

    const onSelect = () => { onChange && onChange(t.track_id); };
    const onPreview = (e) => {
      e.stopPropagation();
      if (isSilence) return;
      toggle(t);
    };

    return (
      <li key={t.track_id}>
        <div
          role="option"
          aria-selected={isSelected}
          tabIndex={0}
          data-testid={`${testIdPrefix}-row-${t.track_id}`}
          onClick={onSelect}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect();
            }
          }}
          style={{
            boxShadow: isSelected
              ? 'inset 0 1px 0 rgba(255,255,255,0.95), 0 1px 0 rgba(197,160,89,0.4), 0 14px 26px -18px rgba(26,54,38,0.38)'
              : 'inset 0 1px 0 rgba(255,255,255,0.85), 0 1px 0 rgba(26,54,38,0.06), 0 6px 14px -10px rgba(26,54,38,0.22)',
            transition: 'box-shadow .2s ease, border-color .2s ease, transform .2s ease, background .2s ease',
          }}
          className={
            'group relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 py-3 sm:px-5 sm:py-4 rounded-lg border cursor-pointer ' +
            (isSelected
              ? 'border-[hsl(var(--accent))] bg-gradient-to-b from-[hsl(var(--accent)/0.08)] to-[hsl(var(--surface))]'
              : 'border-[hsl(var(--primary)/0.15)] bg-gradient-to-b from-[hsl(var(--surface))] to-[hsl(var(--background))] hover:-translate-y-px hover:border-[hsl(var(--primary)/0.35)]')
          }
        >
          {/* Left cluster: play button + copy. Wrapped so the Select
              control can drop below on narrow phones (no overlap). */}
          <div className="flex items-start gap-3 sm:gap-4 min-w-0 w-full sm:flex-1">
          {/* Play / Pause preview button (or mute glyph for silence) */}
          {isSilence ? (
            <span
              aria-hidden
              className="shrink-0 mt-0.5 w-10 h-10 rounded-full flex items-center justify-center text-[hsl(var(--primary)/0.4)] border border-[hsl(var(--primary)/0.15)]"
            >
              <VolumeX size={14} strokeWidth={1.5} />
            </span>
          ) : (
            <button
              type="button"
              onClick={onPreview}
              aria-label={isPlaying ? `Pause ${t.name}` : `Preview ${t.name}`}
              data-testid={`${testIdPrefix}-preview-${t.track_id}`}
              className={
                'shrink-0 mt-0.5 w-10 h-10 rounded-full flex items-center justify-center border transition-all ' +
                (isPlaying
                  ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-white shadow-[0_6px_14px_-6px_rgba(197,160,89,0.7)]'
                  : 'border-[hsl(var(--primary)/0.22)] text-[hsl(var(--primary)/0.75)] hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground,255_255_255)/1)] hover:border-[hsl(var(--primary))]')
              }
            >
              {isPlaying ? (
                <Pause size={14} strokeWidth={2} />
              ) : (
                <Play size={14} strokeWidth={2} style={{ marginLeft: 2 }} />
              )}
            </button>
          )}

          {/* Track copy */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-serif text-[17px] sm:text-[18px] text-[hsl(var(--primary))] leading-tight break-words">
                {t.name}
              </span>
              {isCustom && (
                <span className="text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-full bg-[hsl(var(--accent))] text-white">
                  Your song
                </span>
              )}
            </div>
            {(t.mood || t.duration) && !isCustom && (
              <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.55)] mt-1 font-semibold">
                {[t.mood, t.duration].filter(Boolean).join(' · ')}
              </div>
            )}
            {t.description && (
              <div className="text-[12.5px] text-[hsl(var(--primary)/0.7)] leading-snug mt-1 break-words">
                {t.description}
              </div>
            )}
          </div>
          </div>

          {/* Select button — full-width on phones (sits below the copy),
              compact & right-aligned from sm+. Never overlaps text. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
            data-testid={`${testIdPrefix}-select-${t.track_id}`}
            aria-pressed={isSelected}
            className={
              'shrink-0 self-stretch sm:self-auto inline-flex items-center justify-center gap-1.5 rounded-full px-3.5 py-2 sm:py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold transition-colors border whitespace-nowrap ' +
              (isSelected
                ? 'bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]'
                : 'bg-transparent text-[hsl(var(--primary)/0.75)] border-[hsl(var(--primary)/0.25)] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]')
            }
          >
            {isSelected ? (
              <>
                <Check size={11} strokeWidth={2.5} /> Default
              </>
            ) : (
              'Use this music'
            )}
          </button>
        </div>
      </li>
    );
  };

  // Custom uploaded track sits pinned at the top, OUTSIDE the category
  // grouping, so users always see "their song" first.
  const customRow = customTrack ? {
    kind: 'custom',
    track_id: '__custom__',
    name: customTrack.filename || 'Your uploaded track',
    mood: 'Your song',
    description: 'Uploaded for this memorial — plays on the video and the public memorial.',
    preview_url: customTrack.preview_url || null,
  } : null;

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <div className="ml-overline flex items-center gap-2 mb-3">
        <MusicIcon size={11} strokeWidth={1.5} /> Soundtrack
      </div>

      {customRow && (
        <ul className="space-y-2.5 mb-5" aria-label="Your custom soundtrack">
          {renderRow(customRow)}
        </ul>
      )}

      {orderedCategories.map((cat) => {
        const rowsInCat = grouped.get(cat) || [];
        if (rowsInCat.length === 0) return null;
        const isOpen = openCategory === cat;
        const selectedRow = rowsInCat.find((t) => t.track_id === value);
        const slug = cat.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
        return (
          <section
            key={cat}
            className="mb-2 last:mb-0 border border-[hsl(var(--primary)/0.12)] rounded-lg overflow-hidden bg-gradient-to-b from-[hsl(var(--surface))] to-[hsl(var(--background))]"
            data-testid={`${testIdPrefix}-category-${slug}`}
          >
            {/* iter97.16 — collapsible accordion header. Clicking toggles
                the section. Closed sections show a tiny preview of the
                selected-in-this-category track (if any) plus the count. */}
            <button
              type="button"
              onClick={() => setOpenCategory(isOpen ? null : cat)}
              aria-expanded={isOpen}
              aria-controls={`${testIdPrefix}-category-${slug}-body`}
              data-testid={`${testIdPrefix}-category-${slug}-toggle`}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-[hsl(var(--primary)/0.03)]"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[hsl(var(--primary)/0.7)]">
                    {cat}
                  </span>
                  <span className="text-[9px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--primary)/0.45)]">
                    · {rowsInCat.length} {rowsInCat.length === 1 ? 'track' : 'tracks'}
                  </span>
                  {selectedRow && (
                    <span className="text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-full bg-[hsl(var(--accent))] text-white">
                      Default
                    </span>
                  )}
                </div>
                {selectedRow && !isOpen && (
                  <div className="mt-1 font-serif text-[14px] text-[hsl(var(--primary)/0.85)] truncate">
                    {selectedRow.name}
                  </div>
                )}
              </div>
              <ChevronDown
                size={16}
                strokeWidth={1.8}
                className="shrink-0 text-[hsl(var(--primary)/0.55)] transition-transform"
                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>

            {isOpen && (
              <div
                id={`${testIdPrefix}-category-${slug}-body`}
                className="px-3 pb-3 border-t border-[hsl(var(--primary)/0.08)]"
              >
                <ul className="space-y-2 mt-3" role="listbox" aria-label={cat}>
                  {rowsInCat.map(renderRow)}
                </ul>
              </div>
            )}
          </section>
        );
      })}

      {/* Subtle hint that only one plays at a time */}
      <div className="mt-2.5 text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.4)] text-right font-medium">
        One preview at a time
      </div>
    </div>
  );
}

export default MusicPicker;
